import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import RecentReportItem from "./RecentReportItem";
import LoadingIndicator from "./LoadingIndicator";
import { useLatestReports } from "../hooks/useLatestReports";
import { structuredDataManager } from "../utils/StructuredDataManager";
import { useSidebarState } from "../hooks/useSidebarState";
import { sidebarStateManager } from "../utils/SidebarStateManager";
import { CloseIcon, DocumentOutlineIcon } from "./Icon";
import styles from "./RestaurantDetailsSidebar.module.css";
import { SidebarType } from "../utils/SidebarStateManager";
import { useSidebarOpenEffect } from "../hooks/useSidebarOpenEffect";

interface RecentReportsContentProps {
  initialReports?: any[];
}

const RecentReportsContent: React.FC<RecentReportsContentProps> = ({ initialReports }) => {
  const { t, i18n } = useTranslation();
  const { reports, loading, error, refetch } = useLatestReports(initialReports);
  const { closeSidebar } = useSidebarState(sidebarStateManager);

  // Refetch reports when sidebar opens (only on transition from closed to open)
  useSidebarOpenEffect(SidebarType.RECENT_REPORTS, refetch);

  // Generate aggregated data for SEO
  const aggregatedData = useMemo(() => {
    if (!reports || reports.length === 0) return null;

    const ratingDistribution = new Map<number, number>();
    const recentRestaurants = new Set<string>();

    reports.forEach(report => {
      // Track rating distribution
      if (report.rating) {
        ratingDistribution.set(report.rating, (ratingDistribution.get(report.rating) || 0) + 1);
      }

      // Track restaurants with reports
      if (report.restaurantName) {
        recentRestaurants.add(report.restaurantName);
      }
    });

    return {
      totalReports: reports.length,
      recentRestaurants: Array.from(recentRestaurants).slice(0, 5),
      averageRating: reports.reduce((sum, r) => sum + (r.rating || 0), 0) / reports.filter(r => r.rating).length,
      ratingDistribution: Array.from(ratingDistribution.entries()).sort((a, b) => a[0] - b[0])
    };
  }, [reports]);

  // Add structured data for user reports
  useEffect(() => {
    if (reports && reports.length > 0 && aggregatedData) {
      const reviewsSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Recent Food Safety Reports",
        "description": `Latest ${reports.length} food safety reports from users across Belgium`,
        "numberOfItems": reports.length,
        "itemListElement": reports.slice(0, 10).map((report, index) => ({
          "@type": "Review",
          "position": index + 1,
          "author": {
            "@type": "Person",
            "name": report.username
          },
          "datePublished": report.createdAt,
          "reviewBody": report.reportText?.substring(0, 200) + (report.reportText && report.reportText.length > 200 ? '...' : ''),
          "reviewRating": report.rating ? {
            "@type": "Rating",
            "ratingValue": report.rating,
            "bestRating": 5,
            "worstRating": 1
          } : undefined,
          "itemReviewed": {
            "@type": "Restaurant",
            "name": report.restaurantName || "Restaurant"
          }
        }))
      };

      structuredDataManager.addStructuredData('userReports', reviewsSchema);
    }

    return () => {
      structuredDataManager.removeStructuredData('userReports');
    };
  }, [reports, aggregatedData]);

  const handleRetry = () => {
    refetch();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Main scrollable content area */}
      <div className={`flex-grow overflow-y-auto overflow-x-hidden ${styles.customScrollbar}`} id="sidebar-scroll-container">
        <div className="pl-4 pr-2 pt-4 pb-16" id="sidebar-content">
      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <LoadingIndicator isLoading={loading} />
          <p className="text-gray-400 mt-4 text-sm">
            {t('reports.loading')}
          </p>
        </div>
      )}
      
      {error && !loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4 mb-4 max-w-sm">
            <p className="text-red-400 text-sm text-center">
              {t('reports.error')}: {error}
            </p>
          </div>
          <button
            onClick={handleRetry}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-md"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      )}
      
      {!loading && !error && reports.length === 0 && (
        <div className={`${styles.card} smooth-transition fade-in`}>
          <div className="flex flex-col items-center justify-center py-8">
            <DocumentOutlineIcon className="h-12 w-12 text-gray-500 mb-4" />
            <div className="text-center">
              <p className="text-sm text-gray-500 dark:text-gray-300 font-medium">
                {t('reports.noReports')}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {t('reports.noReportsSubtext')}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {!loading && !error && reports.length > 0 && (
        <>
          {/* Summary Card */}
          <div className={`${styles.card} smooth-transition fade-in`}>
            {/* Header with close button */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
                  Recent Reports
                </h3>
                <span className="text-xs text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/50 rounded-full px-3 py-1 font-medium">
                  {reports.length} {t('reports.reports')}
                </span>
              </div>
              <button
                onClick={closeSidebar}
                className="p-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200"
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>
            
            {/* Aggregated data for SEO */}
            {aggregatedData && (
              <div >
                <h4 className="text-gray-500 dark:text-gray-300 font-medium mb-3 text-left">
                  {t('reports.summary')}
                </h4>
                <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-600 dark:text-blue-300 font-medium">
                      {t('reports.totalReports')}:
                    </span>
                    <span className="text-gray-700 dark:text-gray-200">{aggregatedData.totalReports}</span>
                  </div>
                  {aggregatedData.averageRating > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-blue-600 dark:text-blue-300 font-medium">
                        {t('reports.avgRating')}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-200">{aggregatedData.averageRating.toFixed(1)}/5</span>
                    </div>
                  )}
                  {aggregatedData.recentRestaurants.length > 0 && (
                    <div className="pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
                      <div className="text-blue-600 dark:text-blue-300 font-medium mb-1">
                        {t('reports.activeRestaurants')}:
                      </div>
                      <div className="text-gray-700 dark:text-gray-200 text-left leading-relaxed">
                        {aggregatedData.recentRestaurants.join(', ')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Reports list */}
          <div className="space-y-4">
            {reports.map((report) => (
              <article 
                key={report.id} 
                className="bg-white dark:bg-gray-800/80 rounded-xl p-4 border border-gray-200 dark:border-gray-700/60 shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200"
                itemScope 
                itemType="https://schema.org/Review"
              >
                <RecentReportItem report={report} />
              </article>
            ))}
          </div>

          {/* View all link */}
          <div className="mt-6 text-center">
            <a
              href={`/${i18n.language || 'en'}/recent-reports`}
              className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium hover:underline transition-colors"
            >
              {t('reports.viewAll', 'View all reports')} &rarr;
            </a>
          </div>
        </>
      )}
        </div>
      </div>
    </div>
  );
};

export default RecentReportsContent;